import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore - Vite handles this import
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledMarkdownContainer = styled(Box)(({ theme }) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  '& h1': {
    fontSize: '2.5rem',
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
  },
  '& h2': {
    fontSize: '2rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  '& h3': {
    fontSize: '1.5rem',
  },
  '& h4': {
    fontSize: '1.25rem',
  },
  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.primary,
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.8,
      color: theme.palette.text.primary,
      '&::marker': {
        color: theme.palette.primary.main,
      },
    },
  },
  '& ul': {
    listStyleType: 'disc',
    '& ul': {
      listStyleType: 'circle',
      marginTop: theme.spacing(1),
    },
  },
  '& ol': {
    listStyleType: 'decimal',
  },
  '& strong': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& em': {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& code': {
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    color: theme.palette.primary.light,
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.9em',
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
  },
  '& pre': {
    margin: `${theme.spacing(2)} 0`,
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
      color: 'inherit',
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    margin: `${theme.spacing(2)} 0`,
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.light,
    },
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: `${theme.spacing(3)} 0`,
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: `${theme.spacing(2)} 0`,
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      fontWeight: 600,
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    margin: `${theme.spacing(2)} 0`,
  },
}));

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Detect if content is HTML (starts with HTML tags) or Markdown
  const isHTML = content.trim().startsWith('<') && (content.includes('<p>') || content.includes('<div>') || content.includes('<h1>') || content.includes('<h2>'));

  // If HTML, render directly with proper styling
  if (isHTML) {
    return (
      <StyledMarkdownContainer
        sx={{
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: 3,
            marginBottom: 2,
            fontWeight: 600,
            color: 'text.primary',
            '&:first-of-type': {
              marginTop: 0,
            },
          },
          '& h1': {
            fontSize: '2.5rem',
            borderBottom: '2px solid',
            borderColor: 'divider',
            paddingBottom: 1,
          },
          '& h2': {
            fontSize: '2rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
            paddingBottom: 0.5,
          },
          '& p': {
            marginBottom: 2,
            lineHeight: 1.8,
            color: 'text.primary',
          },
          '& ul, & ol': {
            marginBottom: 2,
            paddingLeft: 4,
            '& li': {
              marginBottom: 1,
              lineHeight: 1.8,
              color: 'text.primary',
            },
          },
          '& strong': {
            fontWeight: 600,
            color: 'text.primary',
          },
          '& em': {
            fontStyle: 'italic',
            color: 'text.secondary',
          },
          '& code': {
            backgroundColor: 'rgba(99, 102, 241, 0.15)',
            color: 'primary.light',
            padding: '2px 6px',
            borderRadius: 1,
            fontSize: '0.9em',
            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          },
          '& pre': {
            margin: '16px 0',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#1e1e1e',
            padding: 2,
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
              color: '#d4d4d4',
            },
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            paddingLeft: 2,
            margin: '16px 0',
            fontStyle: 'italic',
            color: 'text.secondary',
            backgroundColor: 'rgba(99, 102, 241, 0.05)',
            padding: 2,
            borderRadius: 1,
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.light',
            },
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 2,
            margin: '16px 0',
          },
          '& iframe': {
            maxWidth: '100%',
            borderRadius: 2,
            margin: '16px 0',
          },
          '& video': {
            maxWidth: '100%',
            borderRadius: 2,
            margin: '16px 0',
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '16px 0',
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              padding: 1.5,
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              fontWeight: 600,
            },
          },
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  // Otherwise, render as Markdown
  return (
    <StyledMarkdownContainer>
      <ReactMarkdown
        components={{
          h1: ({ node, ...props }) => <Typography variant="h1" component="h1" {...props} />,
          h2: ({ node, ...props }) => <Typography variant="h2" component="h2" {...props} />,
          h3: ({ node, ...props }) => <Typography variant="h3" component="h3" {...props} />,
          h4: ({ node, ...props }) => <Typography variant="h4" component="h4" {...props} />,
          h5: ({ node, ...props }) => <Typography variant="h5" component="h5" {...props} />,
          h6: ({ node, ...props }) => <Typography variant="h6" component="h6" {...props} />,
          p: ({ node, ...props }) => <Typography variant="body1" component="p" {...props} />,
          code: ({ node, inline, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            return !inline && language ? (
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  '& pre': {
                    margin: 0,
                    padding: 2,
                    background: '#1e1e1e !important',
                  },
                }}
              >
                <SyntaxHighlighter
                  language={language}
                  style={vscDarkPlus}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              </Box>
            ) : (
              <Box
                component="code"
                sx={{
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                  color: 'primary.light',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontSize: '0.9em',
                  fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                }}
                {...props}
              >
                {children}
              </Box>
            );
          },
          a: ({ node, ...props }) => (
            <Link
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.light',
                },
              }}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <Box
              component="blockquote"
              sx={{
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                paddingLeft: 2,
                margin: '16px 0',
                fontStyle: 'italic',
                color: 'text.secondary',
                backgroundColor: 'rgba(99, 102, 241, 0.05)',
                padding: 2,
                borderRadius: 1,
              }}
              {...props}
            />
          ),
          ul: ({ node, ...props }) => <Box component="ul" {...props} />,
          ol: ({ node, ...props }) => <Box component="ol" {...props} />,
          li: ({ node, ...props }) => <Box component="li" {...props} />,
          hr: ({ node, ...props }) => (
            <Divider
              sx={{
                my: 3,
                borderColor: 'divider',
              }}
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </StyledMarkdownContainer>
  );
};

export default MarkdownRenderer;

